import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { Modal } from 'components/UI/Modal/Modal'
import { ISelectItem, Select } from 'components/UI/Select/Select'
import { useMemo, useState } from 'react'
import { DealValidationColumnName } from './importer-types'
import type { MissingItem } from './Wizard/services/validateDealsActor'

export const FB_REMOVE_CELL_VALUE = '__FB_REMOVE_CELL_VALUE__'

export interface ValidateModalData {
  missing: MissingItem[]
  found: string[]
  title: string
  itemName: string
  columnName: DealValidationColumnName
  options: ISelectItem<string>[]
  /** Variant options keyed by product ID (variant validation only) */
  optionsByProductId?: Record<number, ISelectItem<string>[]>
}

export interface ValidateModalProps {
  open: boolean
  onClose: () => void
  onValidate: (data: {
    columnName: DealValidationColumnName
    changes: ValidateModalRow[]
  }) => void
  data: ValidateModalData | null
}

export interface ValidateModalRow {
  /** Unique identifier — composite of context + value to disambiguate same-name variants */
  id: string
  original: string
  mapped?: string
  /** Product name for display and product-aware fix */
  context?: string
  /** Product ID for scoped dropdown lookup */
  productId?: number
}

function buildInitialRows(data: ValidateModalData): ValidateModalRow[] {
  return data.missing.map((item) => ({
    id: item.context ? `${item.context}\0${item.value}` : item.value,
    original: item.value,
    mapped: undefined,
    context: item.context,
    productId: item.productId,
  }))
}

/**
 * Outer component handles the null-data guard. When data changes,
 * the key prop remounts ValidateModalContent with fresh initial state.
 */
export function ValidateModal(props: ValidateModalProps) {
  if (props.data === null) {
    return null
  }

  return (
    <ValidateModalContent
      key={
        props.data.columnName + props.data.missing.map((m) => m.value).join(',')
      }
      {...props}
      data={props.data}
    />
  )
}

/**
 * Inner component that owns the mutable mapping state.
 * Remounted via key when validation data changes — no useEffect needed.
 */
function ValidateModalContent({
  data,
  open,
  onClose,
  onValidate,
}: Omit<ValidateModalProps, 'data'> & { data: ValidateModalData }) {
  const [missing, setMissing] = useState<ValidateModalRow[]>(() =>
    buildInitialRows(data)
  )

  const allMapped = useMemo(() => {
    return !missing.some((item) => !item.mapped)
  }, [missing])

  function handleSave() {
    onValidate({
      columnName: data.columnName,
      changes: missing,
    })
  }

  function handleChange(value: string, id: string) {
    setMissing((prev) =>
      prev.map((m) => (m.id === id ? { ...m, mapped: value } : m))
    )
  }

  return (
    <Modal
      size="xl"
      open={open}
      title={data.title}
      description={
        'These items were not found in the database. Please map them to the correct item from the dropdown. Or remove them if they are not needed.'
      }
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
      acceptButtonText={'Save Changes'}
      blockAccept={!allMapped}
      onAccept={handleSave}
    >
      <div className="flex flex-row gap-4 h-[400px]">
        <div className="flex-1">
          <h1>Valid Items</h1>
          <ClientDataTable
            data={data.found.map((item) => ({
              name: item,
            }))}
            columns={[
              {
                header: data.itemName,
                accessorKey: 'name',
              },
            ]}
          />
        </div>

        <div className="flex-1">
          <h1>Invalid Items</h1>
          <ClientDataTable
            data={missing}
            columns={[
              {
                header: 'Original',
                accessorKey: 'original',
                cell: (info) => {
                  const row = info.row.original
                  if (!row.original) {
                    return (
                      <span className="text-fg-secondary italic">(empty)</span>
                    )
                  }
                  if (row.context) {
                    return (
                      <div>
                        <div className="text-fg-secondary text-xxs">
                          {row.context}
                        </div>
                        <div>{row.original}</div>
                      </div>
                    )
                  }
                  return row.original
                },
              },
              {
                header: 'Mapped',
                accessorKey: 'mapped',
                cell: (info) => {
                  const row = info.row.original
                  const items =
                    row.productId != null &&
                    data.optionsByProductId?.[row.productId]
                      ? data.optionsByProductId[row.productId]
                      : (data.options ?? [])
                  return (
                    <Select
                      nullSelectionLabel="Select"
                      items={items}
                      selected={row.mapped}
                      onSelect={(value) => handleChange(value, row.id)}
                    />
                  )
                },
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  )
}
